'use client';

import React, { memo, useEffect, useRef } from 'react';
import { Product, PriceDataDetails } from '../models/Product';
import { CampaignLink } from '@/components/CampaignLink';
import { calculateDiscountPercentage, formatNumberToPriceText } from '@/lib/utils';
import { getEcommerceBodyForProduct, GTMEventType } from '@/lib/GTMEventType';
import { sendGTMEvent } from '@/lib/gtm-wrapper';
import SmartImage from './SmartImage';
import { useInView } from 'react-intersection-observer';
import { sendProductToBatch } from '@/lib/hook/sendProductToBatch';
import clsx from 'clsx';
import { Category } from '@/models/CategoryDetail';
import FavoriteButton from './FavoriteButton';
interface OptimizedProductCardProps {
  product: Product;
  category?: Category;
  categoryName?: string;
  price?: PriceDataDetails;
  priority?: boolean;
  maxWidth?: number;
  className?: string;
  imageClassName?: string;
  hideBadge?: boolean;
  hideCampaign?: () => void;
  listId?: string;
  listName?: string;
  inPopup?: any;
  inTooltip?: any;
  imageWidth?: any;
}

const OptimizedProductCard: React.FC<OptimizedProductCardProps> = memo(
  ({
    product,
    category,
    categoryName,
    price,
    priority = false,
    maxWidth = 214,
    className = '',
    imageClassName = '',
    hideBadge = false,
    hideCampaign,
    listId = '',
    listName = '',
    inPopup = false,
  }) => {
    const { ref: cardRef, inView } = useInView({
      triggerOnce: true,
      threshold: 0.5,
    });

    const hasBeenSent = useRef(false);

    useEffect(() => {
      if (inView && !hasBeenSent.current) {
        hasBeenSent.current = true;
        sendProductToBatch(
          { ...product, category, categoryName },
          category?.id?.toString() || listId,
          category?.title?.toString() || listName,
        );
      }
    }, [inView, product, category, listId, listName, categoryName]);

    const discount =
      typeof price?.priceBeforeDiscount === 'number' && price?.priceBeforeDiscount > price?.price
        ? calculateDiscountPercentage(price.price, price.priceBeforeDiscount)
        : null;

    const handleClick = () => {
      hideCampaign?.();

      const ecommerceData = getEcommerceBodyForProduct(
        { ...product, category, categoryName },
        category?.id?.toString() || listId,
        category?.title?.toString() || listName,
      );

      console.log('GTMMMM');
      sendGTMEvent({ event: GTMEventType.SELECT_ITEM, ecommerce: ecommerceData });
    };

    return (
      <article className={`product-card ${className}`} style={{ maxWidth: `${maxWidth}px` }}>
        <div ref={cardRef} aria-labelledby={`product-id-${product.id}`} className="block w-full">
          <div className="relative">
            <FavoriteButton
              productId={product.id}
              productTitle={product.title}
              productUrl={product.url}
              productImageUrl={product.imageUrl}
              size="sm"
              variant="overlay"
              className="absolute top-2 right-2 z-10"
            />
            <CampaignLink
              href={product.url}
              onClick={handleClick}
              className="block w-full"
            >
              <div className={clsx('product-card-image', inPopup && 'no-border', imageClassName)}>
                <SmartImage
                  src={`${product.imageUrl}`}
                  alt={product.title}
                  fill
                  imageType="product"
                  // priority={priority}
                  priority={false}
                  className="object-cover transition-opacity duration-300 group-hover:opacity-90"
                  quality={75}
                />

                {!hideBadge && price?.badge?.imageUrl && (
                  <div className="absolute top-2 left-2 w-12 h-12">
                    <SmartImage
                      src={`${price.badge.imageUrl}`}
                      alt={price.badge.title || ''}
                      width={48}
                      height={48}
                      imageType="icon"
                      className="object-contain"
                    />
                  </div>
                )}

                {product.inStock === 0 && (
                  <div className="absolute bottom-0 left-0 right-0 flex justify-center pb-3">
                    <span className="bg-white text-[#2B3280] text-xs font-bold px-3 py-1.5 rounded-full shadow">
                      Stokta Yok
                    </span>
                  </div>
                )}
              </div>

              <div className="product-card-content">
                <span id={`product-title-id-${product.id}`} className="product-card-title">
                  {product.title}
                </span>

                <div className="product-card-price-container">
                  {discount && discount > 10 && (
                    <div className="product-card-discount">
                      <span className="product-card-discount-percent">%{discount}</span>
                      <span className="product-card-discount-text">indirim</span>
                    </div>
                  )}

                  <div className="product-card-price-wrapper">
                    {/* Eski fiyat alanı */}
                    <div className="product-card-old-price">
                      {price?.priceBeforeDiscount && discount ? (
                        <div className="product-card-old-price-text">
                          <div className="product-card-old-price-line" />
                          {formatNumberToPriceText(price.priceBeforeDiscount)} TL
                        </div>
                      ) : null}
                    </div>

                    {/* Güncel fiyat alanı */}
                    <div className="product-card-current-price">
                      {price?.price !== undefined ? (
                        `${formatNumberToPriceText(price.price)} TL`
                      ) : (
                        <span className="product-card-loading" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CampaignLink>
          </div>
        </div>
      </article>
    );
  },
);

OptimizedProductCard.displayName = 'OptimizedProductCard';

export default OptimizedProductCard;
